import React from 'react';
import { ExpenseGroup, ExpenseStatus } from '../../../__generated__/graphql';
import { EmptyExpenses } from '../EmptyExpenses';
import {
  Expense,
  ExpenseStatusInfo,
  ExpenseFilter,
  emptyExpenseFilter,
} from '../types';
import { ExpenseGroupTile, UngroupedExpense } from '../ExpenseGroupTile';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Item } from '../useExpenseTableRenderSettings';
import { ExpenseGroupStatusInfo } from '../types';
import {
  collectCategoryFilterOptions,
  collectYearFilterOptions,
} from '../Filters/ExpensesFilter';
import { ExpensesFilterTiles } from '../Filters/ExpensesFilterTiles';
import {
  applyExpenseFilter,
  applyExpenseGroupFilter,
  applyUngroupedExpenseFilter,
} from '../filters';
import { useFlairPersistentFilter } from '../../../../../hooks/useFlairPersistentFilter';
import { FlairLocalStorageKey } from '../../../hooks/useFlairLocalStorage';

export type ExpenseGroupWithRelations = Omit<
  ExpenseGroup,
  'employee' | 'expenses' | 'comments'
> & {
  readonly employee: { readonly Id: string; readonly Name: string };
  readonly expenses: readonly { readonly Id: string }[];
};

type Props = {
  expenses: Expense[];
  expenseGroups: ExpenseGroupWithRelations[];
  itemDisplayOrder?: Array<{ type: 'group' | 'expense'; id: string }>;
  onItemClick: (item: Maybe<Item>) => void;
  updatingItems?: ExpenseStatusInfo[];
  onStatusChange?: (info: ExpenseStatusInfo) => void;
  onExpenseGroupStatusChange?: (info: ExpenseGroupStatusInfo) => Promise<void>;
  onDelete?: (id: string) => void;
  onDeleteExpenseGroup?: (
    expenseGroupId: string,
    expenseGroupName: string,
    childExpenseCount: number,
  ) => void;
  deletingExpenseGroupId?: string;
  submittingExpenseGroupId?: string;
  isMyExpenses?: boolean;
  filterStorageKey: string;
  createRouteUrl: (queryParams: Record<string, string>) => string;
  onCreateBtnClick?: () => void;
  onCreateExpenseGroupBtnClick?: () => void;
  onAddExpense?: (expenseGroupId: string) => void;
  onSubmitExpenseGroup?: (expenseGroupId: string) => void;
  expandedGroups?: Set<string>;
  onExpandedGroupsChange?: (groups: Set<string>) => void;
  filter?: ExpenseFilter;
  setFilter?: (filter: ExpenseFilter) => void;
  invalidExpenseIds?: Set<string>;
};

export const ExpensesTable: React.FC<React.PropsWithChildren<Props>> = ({
  expenses,
  expenseGroups,
  itemDisplayOrder,
  onItemClick,
  onDelete,
  onDeleteExpenseGroup,
  onExpenseGroupStatusChange,
  deletingExpenseGroupId,
  submittingExpenseGroupId,
  isMyExpenses,
  filterStorageKey,
  createRouteUrl,
  onCreateBtnClick,
  onCreateExpenseGroupBtnClick,
  onAddExpense,
  onSubmitExpenseGroup,
  expandedGroups: externalExpandedGroups,
  onExpandedGroupsChange,
  filter: externalFilter,
  setFilter: externalSetFilter,
  invalidExpenseIds,
}) => {
  const [internalFilter, setInternalFilter] =
    useFlairPersistentFilter<ExpenseFilter>({
      defaultFilter: emptyExpenseFilter,
      storageKey: filterStorageKey as FlairLocalStorageKey,
      createRouteUrl,
    });

  // Use external state if provided, otherwise fall back to local state
  const filter = externalFilter || internalFilter;
  const setFilter = externalSetFilter || setInternalFilter;

  const [internalExpandedGroups, setInternalExpandedGroups] = React.useState<
    Set<string>
  >(new Set());
  const expandedGroups = externalExpandedGroups || internalExpandedGroups;
  const setExpandedGroups = onExpandedGroupsChange || setInternalExpandedGroups;

  const filteredExpenses = externalFilter
    ? expenses
    : applyExpenseFilter(filter, expenses);
  const filteredExpenseGroups = externalFilter
    ? expenseGroups
    : applyExpenseGroupFilter(filter, expenseGroups, expenses);
  const ungroupedExpenses = externalFilter
    ? expenses.filter((expense) => !expense.flair__Expense_Group__c)
    : applyUngroupedExpenseFilter(filter, expenses);
  const ungroupedExpenseItems: UngroupedExpense[] = ungroupedExpenses.map(
    (expense) => ({
      Id: expense.id,
      Name: expense.name,
      totalAmount: expense.finalAmount || expense.amount,
      startDate: expense.dateFrom || expense.expenseDate,
      endDate: expense.dateTo,
      flair__Status__c: expense.flair__Status__c,
      flair__Reimbursed__c: expense.reimbursed,
      CurrencyIsoCode: expense.currencyIsoCode,
      employee: {
        Id: expense.employee.Id,
        Name: expense.employee.Name,
      },
      isGroup: false,
      flair__Type__c: '',
      recordType: expense.recordType,
    }),
  );

  const getChildExpensesForGroup = (groupId: string): Expense[] => {
    return filteredExpenses.filter(
      (expense) => expense.flair__Expense_Group__c === groupId,
    );
  };

  const handleGroupTileClick = (
    item: ExpenseGroupWithRelations | UngroupedExpense,
  ) => {
    if (!item.isGroup) {
      onItemClick({ id: item.Id, showComments: false });
    }
  };

  const handleExpenseClick = (
    expense: Expense,
    showComments = false,
    showItems = false,
  ) => {
    if (expense.flair__Expense_Group__c) {
      const newSet = new Set(expandedGroups);
      newSet.add(expense.flair__Expense_Group__c!);
      setExpandedGroups(newSet);
    }
    onItemClick({ id: expense.id, showComments, showItems });
  };

  const handleExpandedChange = (groupId: string, expanded: boolean) => {
    const newSet = new Set(expandedGroups);
    if (expanded) {
      newSet.add(groupId);
    } else {
      newSet.delete(groupId);
    }
    setExpandedGroups(newSet);
  };

  const allGroupedItems: (ExpenseGroupWithRelations | UngroupedExpense)[] =
    React.useMemo(() => {
      let items: (ExpenseGroupWithRelations | UngroupedExpense)[];

      if (itemDisplayOrder && itemDisplayOrder.length > 0) {
        const groupMap = new Map(filteredExpenseGroups.map((g) => [g.Id, g]));
        const expenseMap = new Map(ungroupedExpenseItems.map((e) => [e.Id, e]));

        items = itemDisplayOrder
          .map((item) => {
            if (item.type === 'group') {
              return groupMap.get(item.id);
            } else {
              return expenseMap.get(item.id);
            }
          })
          .filter(
            (item): item is ExpenseGroupWithRelations | UngroupedExpense =>
              item !== undefined,
          );
      } else {
        items = [...filteredExpenseGroups, ...ungroupedExpenseItems];
      }

      return items.sort((a, b) => {
        const isDraftA = a.flair__Status__c === ExpenseStatus.Draft;
        const isDraftB = b.flair__Status__c === ExpenseStatus.Draft;

        if (isDraftA && !isDraftB) return -1;
        if (!isDraftA && isDraftB) return 1;

        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;

        return dateB - dateA;
      });
    }, [itemDisplayOrder, filteredExpenseGroups, ungroupedExpenseItems]);

  if (!expenses.length && !expenseGroups.length) {
    return (
      <>
        <ExpensesFilterTiles
          filter={filter}
          categoryOptions={collectCategoryFilterOptions(expenses)}
          yearOptions={collectYearFilterOptions(expenses)}
          expenses={expenses}
          expenseGroups={expenseGroups}
          onChange={setFilter}
          onCreateBtnClick={onCreateBtnClick}
          onCreateExpenseGroupBtnClick={onCreateExpenseGroupBtnClick}
        />
        <EmptyExpenses />
      </>
    );
  }

  return (
    <div className="mt-5">
      <ExpensesFilterTiles
        filter={filter}
        categoryOptions={collectCategoryFilterOptions(expenses)}
        yearOptions={collectYearFilterOptions(expenses)}
        expenses={expenses}
        expenseGroups={expenseGroups}
        onChange={setFilter}
        onCreateBtnClick={onCreateBtnClick}
        onCreateExpenseGroupBtnClick={onCreateExpenseGroupBtnClick}
      />
      {allGroupedItems.length > 0 && (
        <div className="expense-groups-section mb-4 mt-4">
          {allGroupedItems.map((item) => {
            const childExpenses =
              'flair__Type__c' in item ? getChildExpensesForGroup(item.Id) : [];

            return (
              <ExpenseGroupTile
                key={item.Id}
                item={item as ExpenseGroupWithRelations | UngroupedExpense}
                onClick={handleGroupTileClick}
                childExpenses={childExpenses}
                onExpenseClick={handleExpenseClick}
                isExpanded={expandedGroups.has(item.Id)}
                onExpandedChange={handleExpandedChange}
                isMyExpenses={isMyExpenses}
                onAddExpense={onAddExpense}
                onSubmitExpenseGroup={onSubmitExpenseGroup}
                onDeleteExpenseGroup={onDeleteExpenseGroup}
                onDeleteExpense={onDelete}
                onExpenseGroupStatusChange={onExpenseGroupStatusChange}
                isDeleting={deletingExpenseGroupId === item.Id}
                isSubmitting={submittingExpenseGroupId === item.Id}
                invalidExpenseIds={invalidExpenseIds}
              />
            );
          })}
        </div>
      )}
      {(!allGroupedItems || allGroupedItems.length) === 0 && (
        <div className="mb-8" /> //To ensure the filter section is visible
      )}
    </div>
  );
};
